import React from 'react';
import { useSelector } from 'react-redux';
import Avatar from '../../../base/avatar/components/Avatar';
import { getParticipantDisplayName } from '../../../base/participants/functions';
import useWords from '../../../kamo-si-invite/components/web/useWords';
import { EN } from '../../../kamo-si-invite/copy';
import { useSiCard } from '../../store';
/** Faces shown before "+N". */
const MAX_FACES = 3;
/** Names written before "+N". */
const MAX_NAMES = 2;
/**
 * Pure: the names written, and how many more ("John", "John, Bob"; five people: "John, Bob" and 3 more).
 *
 * @param {string[]} names - First names, in the order the conversations began.
 * @returns {{shown: string, more: number}}
 */
export function chipNames(names) {
    if (names.length <= MAX_NAMES) {
        return { shown: names.join(', '),
            more: 0 };
    }
    return { shown: names.slice(0, MAX_NAMES).join(', '),
        more: names.length - MAX_NAMES };
}
/**
 * TPENGAGE (owner 2026-10-28): on an SI's tile, whom it is listening to right now, live as each conversation starts or
 * ends: a gentle wave, each person's own face (drawn as on their tile: photo, avatar, else initials) and their first
 * names, "+N" past that. Nothing when it is not in a conversation.
 *
 * @returns {ReactElement|null}
 */
export default function SiListeningChip({ participantId }) {
    const card = useSiCard(participantId);
    const w = useWords();
    const people = useSelector((state) => (card?.listening ?? []).map(id => {
        const name = getParticipantDisplayName(state, id) || '';
        return { id,
            first: name.split(' ')[0] || name };
    }), (a, b) => JSON.stringify(a) === JSON.stringify(b));
    const siName = useSelector((state) => getParticipantDisplayName(state, participantId));
    if (!card || !people.length) {
        return null;
    }
    const firsts = people.map(p => p.first || EN['siListening.someone']);
    const { shown, more } = chipNames(firsts);
    const label = more
        ? w('siListening.listeningToMore', { names: shown,
            count: String(more) })
        : w('siListening.listeningTo', { names: shown.replace(/, ([^,]*)$/, ' and $1') });
    return (React.createElement("div", { "aria-label": w('siListening.aria', { si: siName,
            names: firsts.join(', ') }), "aria-live": 'polite', className: 'ksl-chip', role: 'status' },
        React.createElement("span", { "aria-hidden": 'true', className: 'ksl-wave' },
            React.createElement("i", null),
            React.createElement("i", null),
            React.createElement("i", null)),
        React.createElement("span", { "aria-hidden": 'true', className: 'ksl-faces' },
            people.slice(0, MAX_FACES).map(p => (React.createElement("span", { className: 'ksl-face', key: p.id },
                React.createElement(Avatar, { participantId: p.id, size: 20 })))),
            people.length > MAX_FACES && React.createElement("span", { className: 'ksl-more' },
                "+",
                people.length - MAX_FACES)),
        React.createElement("span", { className: 'ksl-text' }, label)));
}
